import { BookmarkBook } from '@/icons/BookmarkBook'
import { BookmarkBookClose } from '@/icons/BookmarkBookClose'
import { CertificateIconClose } from '@/icons/CertificateIconClose'
import { CertificateIconOpen } from '@/icons/CertificateIconOpen'
import { FavoriteTraining } from '@/icons/FavoriteTraining'
import { FavoriteTrainingClose } from '@/icons/FavoriteTrainingClose'
import { HeadSetClose } from '@/icons/HeadSetClose'
import { HeadSetOpen } from '@/icons/HeadSetOpen'
import { Home } from '@/icons/Home'
import { HomeClose } from '@/icons/HomeClose'
import { Participants } from '@/icons/Participants'
import { ParticipantsClose } from '@/icons/ParticipantsClose'
import { ReportingLicensesSidebar } from '@/icons/ReportingLicensesSidebar'
import { SearchTenantIcon } from '@/icons/SearchTenantIcon'
import { SubsidiariesSidebar } from '@/icons/SubsidiariesSidebar'
import { Administration } from '@/icons/Administration'
import type { TenantDto } from '@/services/api/tenant/tenantsEnhanced'

// ==================== TYPES ====================

export type SidebarSectionKey = 'customerManagement' | 'globalAdministration' | 'learningManagement' | 'myAccount'

export type SidebarItemKey =
  | 'customerOverview'
  | 'courseCatalogue'
  | 'dashboard'
  | 'learningHome'
  | 'learningAdministration'
  | 'learningLearners'
  | 'learningTrainings'
  | 'reportingLicenses'
  | 'subsidiaries'
  | 'myCourses'
  | 'myCertificates'

export type SidebarItem = {
  key: SidebarItemKey
  label: string
  path?: string
}

export type SidebarSection = {
  key: SidebarSectionKey
  title: string
  subtitle?: string
  items: SidebarItem[]
}

export type SidebarProps = {
  companyName?: string
  userName?: string
  activeItem?: SidebarItemKey
  onNavigate?: () => void
  initialOpenSection?: SidebarSectionKey
  isOpen?: boolean
  visibleItems?: SidebarItemKey[]
  tenantSubdomain?: string
  tenantsList?: TenantDto[]
  onForceOpen?: () => void
}

// ==================== CONSTANTS ====================

export const globalAdministrationItems: SidebarItem[] = [
  {
    key: 'reportingLicenses',
    label: 'reporting_licenses',
    path: '/global-admin/reporting-licenses',
  },
  {
    key: 'subsidiaries',
    label: 'subsidiaries',
    path: '/global-admin/subsidiaries',
  },
]

export const customerManagementItems: SidebarItem[] = [
  {
    key: 'customerOverview',
    label: 'customerOverview',
    path: '/tenants',
  },
  {
    key: 'courseCatalogue',
    label: 'courseCatalogue',
    path: '/courses',
  },
  {
    key: 'dashboard',
    label: 'dashboard',
    path: '/courses',
  },
]

export const learningManagementItems: SidebarItem[] = [
  {
    key: 'learningHome',
    label: 'home_sidebar',
    path: '/home',
  },
  {
    key: 'learningLearners',
    label: 'participants',
    path: '/participants',
  },
  {
    key: 'learningTrainings',
    label: 'trainings',
    path: '/trainings',
  },
  {
    key: 'learningAdministration',
    label: 'administration_admin',
    path: '/administration',
  },
]

export const myAccountItems: SidebarItem[] = [
  {
    key: 'myCourses',
    label: 'my_courses_nav',
    path: '/my/courses',
  },
  {
    key: 'myCertificates',
    label: 'my_certificates_nav',
    path: '/my/certificates',
  },
]

const sidebarItemsBySection: Record<SidebarSectionKey, SidebarItem[]> = {
  customerManagement: customerManagementItems,
  globalAdministration: globalAdministrationItems,
  learningManagement: learningManagementItems,
  myAccount: myAccountItems,
}

const sidebarSectionOrder: SidebarSectionKey[] = [
  'customerManagement',
  'globalAdministration',
  'learningManagement',
  'myAccount',
]

const sidebarItemsInOrder: SidebarItem[] = sidebarSectionOrder.flatMap(
  (sectionKey) => sidebarItemsBySection[sectionKey],
)

// ==================== ICON UTILS ====================

const defaultMenuItemIcon = { open: BookmarkBook, close: BookmarkBookClose }

const menuItemIcons: Record<SidebarItemKey, { open: React.ComponentType; close: React.ComponentType }> = {
  customerOverview: { open: HeadSetOpen, close: HeadSetClose },
  courseCatalogue: { open: BookmarkBook, close: BookmarkBookClose },
  dashboard: { open: SearchTenantIcon, close: SearchTenantIcon },
  learningHome: { open: Home, close: HomeClose },
  learningAdministration: { open: Administration, close: Administration },
  learningLearners: { open: Participants, close: ParticipantsClose },
  learningTrainings: { open: FavoriteTraining, close: FavoriteTrainingClose },
  reportingLicenses: { open: ReportingLicensesSidebar, close: ReportingLicensesSidebar },
  subsidiaries: { open: SubsidiariesSidebar, close: SubsidiariesSidebar },
  myCourses: { open: FavoriteTraining, close: FavoriteTrainingClose },
  myCertificates: { open: CertificateIconOpen, close: CertificateIconClose },
}

export const getMenuItemIcon = (itemKey: SidebarItemKey, isCollapsed: boolean): JSX.Element => {
  const iconPair = menuItemIcons[itemKey] || defaultMenuItemIcon
  const Icon = isCollapsed ? iconPair.close : iconPair.open
  return <Icon />
}

const isGermanLanguage = (language?: string): boolean => language?.toLowerCase().startsWith('de') ?? false

export const getSectionInitial = (sectionKey: SidebarSectionKey, language?: string): string => {
  const isGerman = isGermanLanguage(language)

  switch (sectionKey) {
    case 'customerManagement':
      return 'BO'
    case 'globalAdministration':
      return isGerman ? 'GL' : 'GA'
    case 'learningManagement':
      return isGerman ? 'LV' : 'LM'
    case 'myAccount':
      return isGerman ? 'MK' : 'MA'
    default:
      return ''
  }
}

// ==================== NAVIGATION UTILS ====================

export const resolveActiveItem = (pathname: string): SidebarItemKey => {
  const matchingItem = sidebarItemsInOrder.find((item) => item.path && pathname.startsWith(item.path))
  return (matchingItem?.key as SidebarItemKey) || 'reportingLicenses'
}

export const resolveInitialOpenSection = (activeItem: SidebarItemKey): SidebarSectionKey => {
  const section = sidebarSectionOrder.find((sectionKey) =>
    sidebarItemsBySection[sectionKey].some((item) => item.key === activeItem),
  )

  if (section) {
    return section
  }

  return 'globalAdministration'
}
